/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';

import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { DateTime } from '../../../core/utils';
import {
    setGhostModeAction,
    setGuideHighlightToInitialPositionAction,
    setHorizontalPageCountAction,
    setHorizontalPositionAction,
    setHorizontalSessionAction,
    setVerticalPageCountAction,
    setVerticalPositionAction,
} from './guidehighlight.actions';
import {
    SET_GHOST_MODE,
    SET_GUIDE_HIGHLIGHT_TO_INITIAL_POSITION,
    SET_HORIZONTAL_PAGE_COUNT,
    SET_HORIZONTAL_POSITION,
    SET_HORIZONTAL_SESSION,
    SET_VERTICAL_AND_HORIZONTAL_POSITION,
    SET_VERTICAL_PAGE_COUNT,
    SET_VERTICAL_POSITION,
} from './guidehighlight.types';

const initialState: {
    ghostMode: boolean;
    horizontalPageCount: number;
    horizontalPosition: number;
    horizontalSession: DateTime;
    verticalPageCount: number;
    verticalPosition: number;
} = {
    ghostMode: false,
    horizontalPageCount: 0,
    horizontalPosition: 1,
    horizontalSession: new DateTime(),
    verticalPageCount: 0,
    verticalPosition: 0,
};

const guideHighlightReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SET_GHOST_MODE:
            return { ...state, ghostMode: payload.ghostMode };
        case SET_GUIDE_HIGHLIGHT_TO_INITIAL_POSITION:
            return {
                ...state,
                horizontalPageCount: payload.horizontalPageCount,
                horizontalPosition: payload.horizontalPosition,
                horizontalSession: payload.horizontalSession,
            };
        case SET_HORIZONTAL_PAGE_COUNT:
            return {
                ...state,
                horizontalPageCount: payload.horizontalPageCount,
            };
        case SET_HORIZONTAL_POSITION:
            return {
                ...state,
                horizontalPosition: payload.horizontalPosition,
            };
        case SET_HORIZONTAL_SESSION:
            return {
                ...state,
                horizontalSession: payload.horizontalSession,
            };
        case SET_VERTICAL_AND_HORIZONTAL_POSITION:
            return {
                ...state,
                horizontalPosition: payload.horizontalPosition,
                verticalPosition: payload.verticalPosition,
            };
        case SET_VERTICAL_PAGE_COUNT:
            return {
                ...state,
                verticalPageCount: payload.verticalPageCount,
            };
        case SET_VERTICAL_POSITION:
            return {
                ...state,
                verticalPosition: payload.verticalPosition,
            };
        default:
            return state;
    }
};

export const disableGhostMode = () => (dispatch: Function) => {
    dispatch(setGhostModeAction(false));
};

export const enableGhostMode = () => (dispatch: Function) => {
    dispatch(setGhostModeAction(true));
};

export const setGuideHighlightToInitialPosition = (startTime: DateTime) => (dispatch: Function) => {
    dispatch(setGuideHighlightToInitialPositionAction(startTime));
};

export const setHorizontalPageCount = (horizontalPageCount: number) => (dispatch: Function) => {
    dispatch(setHorizontalPageCountAction(horizontalPageCount));
};

export const setHorizontalPosition = (horizontalPosition: number) => (dispatch: Function) => {
    dispatch(setHorizontalPositionAction(horizontalPosition));
    dispatch(setHorizontalSession());
};

export const setHorizontalSession = () => (dispatch: Function, getState: TGetState) => {
    const { gridGuide, guideHighlight } = getState();
    const { horizontalPosition, verticalPosition } = guideHighlight;
    const { guideRows, currentTimestamp } = gridGuide;

    if (horizontalPosition > 0 && guideRows && guideRows[verticalPosition]) {
        const { guideCells } = guideRows[verticalPosition];

        if (guideCells && guideCells[horizontalPosition - 1]) {
            const { startTime } = guideCells[horizontalPosition - 1];
            if (startTime.getTime() < currentTimestamp.getTime())
                dispatch(setHorizontalSessionAction(currentTimestamp));
            else dispatch(setHorizontalSessionAction(startTime));
        }
    }
};

export const setVerticalPageCount = (verticalPageCount: number) => (dispatch: Function) => {
    dispatch(setVerticalPageCountAction(verticalPageCount));
};

export const setVerticalPosition = (verticalPosition: number) => (dispatch: Function) => {
    dispatch(setVerticalPositionAction(verticalPosition));
};

export default guideHighlightReducer;
